import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';
import { Form } from 'react-bootstrap';

/**
 * Simple autocomplete input using react-select
 */
const AutoCompletableInput = ({
    value,
    onChange,
    options = [],
    onInputChange,
    isLoading = false,
    placeholder = "Search...",
    label,
    isInvalid = false,
    errorMsg = '',
    disabled = false,
    displayKey = 'label',
    valueKey = 'value',
    onSelect,
    clearable = true,
    minimumInputLength = 2
}) => {
    const [menuIsOpen, setMenuIsOpen] = useState(false);
    
    // Transform options to react-select format
    const selectOptions = useMemo(() => {
        if (!options || !Array.isArray(options)) {
            return [];
        }
        return options.map(option => ({
            value: option[valueKey],
            label: option[displayKey],
            data: option
        }));
    }, [options, valueKey, displayKey]);

    // Find current value option
    const currentValue = useMemo(() => {
        if (!value) return null;
        
        // First try to find in current options
        const existingOption = selectOptions.find(option => option.value === value);
        if (existingOption) return existingOption;
        
        // If not found in options but we have a value, create a temporary option
        // This ensures the value is displayed even when options are not loaded
        return {
            value: value,
            label: value,
            data: { [valueKey]: value, [displayKey]: value }
        };
    }, [value, selectOptions, valueKey, displayKey]);

    // Handle input change (for search)
    const handleInputChange = useCallback((inputValue, actionMeta) => {
        if (actionMeta.action === 'input-change') {
            onChange(inputValue);
            
            // Only trigger search if we have enough characters
            if (onInputChange && inputValue && inputValue.length >= minimumInputLength) {
                onInputChange(inputValue);
                setMenuIsOpen(true);
            } else {
                setMenuIsOpen(false);
            }
        }
    }, [onChange, onInputChange, minimumInputLength]);

    // Handle option selection
    const handleChange = useCallback((selectedOption, actionMeta) => {
        if (actionMeta.action === 'select-option') {
            if (onSelect) {
                onSelect(selectedOption.data);
            }
            onChange(selectedOption.label);
            setMenuIsOpen(false);
        } else if (actionMeta.action === 'clear') {
            onChange('');
            setMenuIsOpen(false);
        }
    }, [onSelect, onChange]);

    // Handle menu state changes
    const handleMenuOpen = useCallback(() => {
        setMenuIsOpen(true);
    }, []);

    const handleMenuClose = useCallback(() => {
        setMenuIsOpen(false);
    }, []);

    // Prevent form submission on key events
    const handleKeyDown = useCallback((event) => {
        // Only prevent Enter key when menu is closed to avoid form submission
        // Allow Enter key when menu is open for option selection
        if (event.key === 'Enter' && !menuIsOpen) {
            event.preventDefault();
            event.stopPropagation();
        }
    }, [menuIsOpen]);

    return (
        <Form.Group>
            {label && <Form.Label>{label}</Form.Label>}
            <Select
                value={currentValue}
                options={selectOptions}
                onInputChange={handleInputChange}
                onChange={handleChange}
                onMenuOpen={handleMenuOpen}
                onMenuClose={handleMenuClose}
                onKeyDown={handleKeyDown}
                menuIsOpen={menuIsOpen && (selectOptions.length > 0 || isLoading)}
                isLoading={isLoading}
                placeholder={placeholder}
                isDisabled={disabled}
                isClearable={clearable}
                isSearchable={true}
                noOptionsMessage={() => "No results found"}
                loadingMessage={() => "Loading..."}
                className={isInvalid ? 'is-invalid' : ''}
                classNamePrefix="react-select"
                menuPlacement="auto"
                menuPosition="absolute"
                styles={{
                    control: (base) => ({
                        ...base,
                        borderColor: isInvalid ? '#dc3545' : base.borderColor,
                        '&:hover': {
                            borderColor: isInvalid ? '#dc3545' : base.borderColor
                        },
                        margin: '0',
                        padding: '0.5rem',
                        borderRadius: '0.375rem',
                        '&:focus': {
                            borderColor: isInvalid ? '#dc3545' : base.borderColor,
                            margin: '0',
                            padding: '0.5rem',
                            borderRadius: '0.375rem'
                        }
                    }),
                    input: (base) => ({
                        ...base,
                        padding: '0',
                        margin: '0'
                    }),
                    container: (base) => ({
                        ...base,
                        padding: '0',
                        margin: '0'
                    }),
                    valueContainer: (base) => ({
                        ...base,
                        padding: '0',
                        margin: '0'
                    }),
                    menu: (base) => ({
                        ...base,
                        zIndex: 9999,
                        padding: '0',
                        margin: '0'
                    })
                }}
            />
            {isInvalid && errorMsg && (
                <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                    {errorMsg}
                </Form.Control.Feedback>
            )}
        </Form.Group>
    );
};

AutoCompletableInput.propTypes = {
    value: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    options: PropTypes.array,
    onInputChange: PropTypes.func,
    isLoading: PropTypes.bool,
    placeholder: PropTypes.string,
    label: PropTypes.string,
    isInvalid: PropTypes.bool,
    errorMsg: PropTypes.string,
    disabled: PropTypes.bool,
    displayKey: PropTypes.string,
    valueKey: PropTypes.string,
    onSelect: PropTypes.func,
    clearable: PropTypes.bool,
    minimumInputLength: PropTypes.number
};

export default AutoCompletableInput; 